import { FC, useEffect, useMemo } from 'react';
import { Button, Confirmation } from '@storybook';

import { EmailClientsState, IsEmailClientsListLoadingState, useEmailClient } from 'views/email-client/stores';
import { useDeleteAction, useClientActions } from './hooks';
import { LoadingSkeleton, SettingsView, MainView } from './components';
import './connected-client.scss';
import { useRecoilValue } from 'recoil';

interface IConnectedClient {
	handleClick: () => void;
	isSettingPage?: boolean;
}

export const ConnectedClient: FC<IConnectedClient> = ({
	handleClick,
	isSettingPage = false,
}) => {
	const emailClientsList = useRecoilValue(EmailClientsState);
	const isLoading = useRecoilValue(IsEmailClientsListLoadingState);
	const { getEmailClients } = useEmailClient();
	
	const { deleteAction, openDeleteModal, handleConfirmationModal } = useDeleteAction();
	const { getDropOptions } = useClientActions({
		onEdit: handleClick,
		onDelete: openDeleteModal,
	});

	useEffect(() => {
		getEmailClients();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderContent = useMemo(() => {
		if (isLoading) {
			return <LoadingSkeleton isSettingPage={isSettingPage} />;
		}

		if (isSettingPage) {
			return (
				<SettingsView
					emailClientsList={emailClientsList}
					handleClick={handleClick}
					getDropOptions={getDropOptions}
				/>
			);
		}

		if (emailClientsList.length === 0) {
			return null;
		}

		return (
			<MainView
				emailClientsList={emailClientsList}
				handleClick={handleClick}
			/>
		);
	}, [isLoading, isSettingPage, emailClientsList, handleClick, getDropOptions]);

	const confirmationMessage = useMemo(() => {
		if(emailClientsList.length === 1) {
			return 'This is your only configured email client. If you remove it, your account will automatically switch to the Simplici Email Client as the default.';
		}
		if(deleteAction.usedInComplexFlow === 0) {
			return 'This email client is not used in any onboarding flows. You can safely remove it from your account.';
		}
		return `This email client is currently used in ${deleteAction.usedInComplexFlow} onboarding flows. If you remove it, those flows will automatically switch to the current default email client.`;
	}, [deleteAction.usedInComplexFlow, emailClientsList.length]);

	return (
		<>
			{emailClientsList.length === 0 && !isLoading && !isSettingPage && (
				<Button
					handleClick={handleClick}
					label={'Add your email client'}
					type="button__filled button__filled--secondary btn-add-email-client"
				/>
			)}
			
			{renderContent}
			
			<Confirmation
				visible={deleteAction.modalOpen}
				title="Remove Email Client?"
				description={confirmationMessage}
				boldDescription=""
				handleModal={handleConfirmationModal}
				label="Yes, Remove"
				secondryLabel="Cancel"
				loading={deleteAction.loading}
			/>
		</>
	);
};
